import { LockOutlined } from '@ant-design/icons';
import { Col, Divider, Form, Radio, Row, Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScopeButton } from '../components/buttons';
import { FormInput } from '../components/formInputs';
import type { NewMember } from './types';

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
};

type AddMemberFormProps = {
  saveNewMember: (newMember: NewMember) => void;
  isClientDisabled?: boolean;
  onClientLockedClick?: () => void;
};

export const AddMemberForm = ({
  saveNewMember,
  isClientDisabled = false,
  onClientLockedClick,
}: AddMemberFormProps) => {
  const { t } = useTranslation();
  const [isClient, setIsClient] = useState(false);

  const handleSubmit = (values: FormValues) => {
    saveNewMember({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      isClient,
    });
  };

  return (
    <Form
      name="add-member"
      autoComplete="off"
      onFinish={handleSubmit}
      layout="vertical"
    >
      <Row justify="center">
        <Col span={24}>
          <Radio.Group
            value={isClient}
            buttonStyle="solid"
            onChange={(e) => setIsClient(e.target.value)}
          >
            <Radio.Button value={false}>{t('users.user')}</Radio.Button>
            <Tooltip
              title={
                isClientDisabled
                  ? t('vat-report.unlock-full-workflow-with-client-approval')
                  : undefined
              }
              placement="bottom"
              arrow={false}
            >
              <span>
                <Radio.Button
                  value
                  disabled={isClientDisabled}
                  onClick={(e) => {
                    if (isClientDisabled) {
                      e.preventDefault();
                      onClientLockedClick?.();
                    }
                  }}
                >
                  {t('main.client')}{' '}
                  {isClientDisabled && (
                    <LockOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                        onClientLockedClick?.();
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </Radio.Button>
              </span>
            </Tooltip>
          </Radio.Group>
          <p
            style={{
              margin: '12px 0 0',
              fontSize: 12,
              color: 'var(--color-text-muted, #5a6a6a)',
              lineHeight: '18px',
            }}
          >
            {isClient
              ? t('users.member-type-client-description')
              : t('users.member-type-user-description')}
          </p>
          {isClientDisabled && !isClient && (
            <p
              style={{
                margin: '8px 0 0',
                fontSize: 12,
                color: 'var(--color-text-muted, #5a6a6a)',
                lineHeight: '18px',
              }}
            >
              {t('users.member-type-client-plan-hint')}
            </p>
          )}
        </Col>
        <Divider />
        <Col span={24}>
          <span className="label-text">{t('signup.first-name')}</span>
          <Form.Item
            name="firstName"
            rules={[{ required: true, message: t('common.required-field') }]}
          >
            <FormInput width="100%" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <span className="label-text">{t('signup.last-name')}</span>
          <Form.Item
            name="lastName"
            rules={[{ required: true, message: t('common.required-field') }]}
          >
            <FormInput width="100%" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <span className="label-text">{t('signup.email-address')}</span>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: t('common.required-field') },
              { type: 'email', message: t('signup.invalid-email') },
            ]}
          >
            <FormInput width="100%" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item>
            <div className="rows-drawer-footer">
              <ScopeButton type="primary" htmlType="submit" width="100%">
                {t('users.send-invitation')}
              </ScopeButton>
            </div>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
